import { Button } from '@consigli/facade';
import {
  useUpdateFindingMutation,
  useProjectId,
  usePackageId,
  useServiceName,
  usePackageByTypeAndPackageId,
} from '@consigli/hooks';
import { Finding, FindingStatus, DocumentPackageType } from '@consigli/types';
import clsx from 'clsx';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuSend } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { useViewer } from '@/features/document-viewer/use-viewer';
import { ProcessingPopup } from '@/pages/insight/findings/components/processing-popup';
import { mapActionNameToStates } from '@/pages/insight/findings/findings-content';
import { useFindingsContext } from '@/pages/insight/findings/findings-context';
import { ServiceIdIndexValue } from '@/routes';
import { useDemoMode } from '@/util/demo-mode';

type UpdateFindingButtonsProps = {
  finding: Finding;
  itemsOnPage: number;
  className?: string;
};

export const UpdateFindingButtons: FC<UpdateFindingButtonsProps> = ({
  finding,
  itemsOnPage,
  className,
}) => {
  const { t } = useTranslation();
  const [updateFinding] = useUpdateFindingMutation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const serviceName = useServiceName();
  const { close } = useViewer();
  const [isForwardFindingOpen, setForwardFindingOpen] = useState(false);
  const { page, setPage } = useFindingsContext();
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );
  const isPublished = pkg ? pkg.published : false;

  const filteredButtonKeys = useMemo(() => {
    // Match tab order; PROCESSING is included but opens the forward popup
    // Tabs: ACTIVE, PROCESSING, PROCESSED, NOT_RELEVANT, DELETED
    const preferredButtonKeysOrder = [
      FindingStatus.ACTIVE,
      FindingStatus.PROCESSING,
      FindingStatus.PROCESSED,
      FindingStatus.NOT_RELEVANT,
      FindingStatus.DELETED,
    ];
    let buttonKeys = Object.keys(mapActionNameToStates).filter(
      (item: string) => item !== finding.status,
    );
    // Hide delete button if admin features are disabled OR if document package is published
    if (!adminFeaturesEnabled || isPublished) {
      buttonKeys = buttonKeys.filter((item: string) => item !== FindingStatus.DELETED);
    }
    return buttonKeys.sort((a, b) => {
      const indexA = preferredButtonKeysOrder.indexOf(a as FindingStatus);
      const indexB = preferredButtonKeysOrder.indexOf(b as FindingStatus);
      return indexA - indexB;
    });
  }, [finding.status, adminFeaturesEnabled, isPublished]);

  const updateIndividualFinding = useCallback(
    async (finding: Finding, status: FindingStatus) => {
      try {
        if (itemsOnPage === 1 && page > 1) {
          setPage((prevPage) => prevPage - 1);
        }
        await updateFinding({
          projectId,
          packageId,
          findingId: finding.id,
          data: {
            status,
          },
        }).unwrap();
        toast.info(t(`toast.file-update-success-no-status`));
      } catch (e) {
        console.log('Error updating finding:', e);
        toast.info(t(`toast.file-update-failure`));
      }
    },
    [updateFinding, projectId, packageId, itemsOnPage, page, t, setPage],
  );

  return (
    <>
      <div className={clsx('flex flex-wrap gap-2', className)}>
        {filteredButtonKeys.map((key: string) => {
          const isProcessing = key === FindingStatus.PROCESSING;
          return (
            <Button
              rounded
              iconOnly
              secondary
              key={key}
              className="border-1"
              title={t(mapActionNameToStates[key].title)}
              icon={isProcessing ? LuSend : mapActionNameToStates[key].icon}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                if (isProcessing) {
                  setForwardFindingOpen(true);
                  return;
                }
                if (finding) {
                  updateIndividualFinding(finding, mapActionNameToStates[key].status);
                }
                close();
              }}
            />
          );
        })}
      </div>
      {isForwardFindingOpen && finding && (
        <ProcessingPopup
          onClose={() => setForwardFindingOpen(false)}
          findings={[{ ...finding, checked: true }]}
          count={1}
          itemsOnPage={itemsOnPage}
        />
      )}
    </>
  );
};
